import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  PermissionAction,
  PermissionResource,
  useGetOrganization,
} from '@sib/api';
import { PermissionBoundary, useCan, useUserSetupStore } from '@sib/auth';
import { useTranslation } from '@sib/i18n';
import {
  Button,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormTagField,
  useParsedTagErrors,
} from '@sib/ui';
import { Tooltip } from '@sib/ui';
import { Info, X } from '@sib/ui/icons';
import { Invitee } from '@spendbrain/types/user';
import { InviteesList } from '../InviteesList';
import { ShareDialogFormMessage } from '../ShareDialogFormMessage';
import {
  MESSAGE_VISIBILITY_TIMEOUT,
  ShareDialogFormField,
  ShareDialogFormValues,
  useOnSubmit,
} from '../ShareDialog.utils';

interface ShareDialogViewProps {
  formProps: UseFormReturn<ShareDialogFormValues>;
  onInviteesAdd: (invitees: Invitee[]) => void;
  onModalClose: () => void;
  isListLoading: boolean;
  invitees: Invitee[];
  openResendInvitationDialog: (invitee: Invitee) => void;
  openRemoveAccessDialog: (invitee: Invitee) => void;
}

export const ShareDialogView = ({
  formProps,
  onInviteesAdd,
  onModalClose,
  isListLoading,
  invitees,
  openResendInvitationDialog,
  openRemoveAccessDialog,
}: ShareDialogViewProps) => {
  const { t } = useTranslation();
  const can = useCan();
  const organizationId = useUserSetupStore(state => state.organization.id);
  const { data: organization } = useGetOrganization(organizationId);
  const organizationDomains = organization?.domains ?? [];

  const {
    formState: {
      errors: { emailAddresses: error },
    },
    getValues,
    reset,
  } = formProps;
  const {
    onSubmit,
    isPending: isSubmitting,
    errorMessage,
    successMessage,
  } = useOnSubmit({
    onSuccess: (invitees: Invitee[]) => {
      onInviteesAdd(invitees);
      reset();
    },
  });

  const errors = useParsedTagErrors(error);

  const formErrorMessage = errors[0];
  const emails = getValues(ShareDialogFormField.EmailAddresses);

  const isSubmitDisabled =
    errors.some(error => error.variant === 'error') || emails.length === 0;

  const submitWithWarnings = useCallback(() => {
    if (isSubmitDisabled) return;
    onSubmit({
      emailAddresses: getValues()[ShareDialogFormField.EmailAddresses],
    });
  }, [onSubmit, getValues, isSubmitDisabled]);

  return (
    <>
      <DialogHeader>
        <Button
          onClick={onModalClose}
          size="icon"
          variant="ghost"
          className="text-primary absolute top-4 right-2 sm:top-7 sm:right-10"
        >
          <X className="text-primary" />
        </Button>
        <DialogTitle className="text-primary text-center text-[38px] leading-[38px] font-semibold sm:leading-[28px]">
          {t('components.shareDialog.title')}
        </DialogTitle>
        <div className="flex items-center justify-center gap-2 pt-4">
          <DialogDescription className="text-muted-foreground text-center text-lg leading-[28px] font-semibold sm:text-xl">
            {t('components.shareDialog.subtitle')}
          </DialogDescription>
          {organizationDomains.length > 0 && (
            <Tooltip
              content={t('components.shareDialog.tooltips.allowedDomains', {
                domains: organizationDomains.join(', '),
              })}
              TooltipContentProps={{
                variant: 'secondary',
                className: 'text-xs max-w-[320px] p-2.5',
              }}
            >
              <Info className="text-muted-foreground size-4" />
            </Tooltip>
          )}
        </div>
        <PermissionBoundary
          permissionsRequired={{
            resources: PermissionResource.AccessShare,
            actions: PermissionAction.CREATE,
          }}
        >
          <div className="py-10">
            <div className="h-5 w-full">
              {errorMessage || successMessage ? (
                <ShareDialogFormMessage
                  message={errorMessage || successMessage || ''}
                  variant={errorMessage ? 'error' : 'success'}
                  timeout={MESSAGE_VISIBILITY_TIMEOUT}
                />
              ) : (
                formErrorMessage && (
                  <ShareDialogFormMessage
                    message={formErrorMessage?.message || ''}
                    variant={
                      formErrorMessage?.variant === 'error'
                        ? 'error'
                        : 'warning'
                    }
                  />
                )
              )}
            </div>
          </div>
          <div className="shadow-[0px_4px_6px_0px_rgba(0,0,0,0.09) flex flex-col items-start gap-2 rounded-sm border p-3.5">
            <p className="text-primary text-sm leading-4.5 font-semibold">
              {t('components.shareDialog.invite')}
            </p>
            <div className="w-full">
              <Form onSubmit={onSubmit} {...formProps}>
                <div className="border-border flex w-full items-start gap-2 sm:gap-4">
                  <div className="flex-[1_0]">
                    <FormTagField
                      placeholder={t('components.shareDialog.fieldPlaceholder')}
                      autoComplete="off"
                      name={ShareDialogFormField.EmailAddresses}
                    />
                  </div>
                  <Button
                    className="h-10 px-1 sm:px-4"
                    type="button"
                    loading={isSubmitting}
                    onClick={submitWithWarnings}
                    disabled={isSubmitDisabled || isListLoading}
                  >
                    {t('components.shareDialog.invite')}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </PermissionBoundary>
      </DialogHeader>
      <PermissionBoundary
        permissionsRequired={{
          resources: PermissionResource.AccessShare,
          actions: PermissionAction.READ,
        }}
      >
        <InviteesList
          {...(can({
            permissions: {
              resources: PermissionResource.AccessShare,
              actions: PermissionAction.CREATE,
            },
          }) && {
            onResend: openResendInvitationDialog,
          })}
          {...(can({
            permissions: {
              resources: PermissionResource.AccessShare,
              actions: PermissionAction.DELETE,
            },
          }) && {
            onRemove: openRemoveAccessDialog,
          })}
          data={invitees}
          loading={isListLoading}
        />
      </PermissionBoundary>
    </>
  );
};
